import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, configAPI } from '../services/api';

const AuthContext = createContext(null);
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('JagdishNet');
  const [showLogo, setShowLogo] = useState(true);
  const timerRef = useRef(null);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await configAPI.get();
      if (data?.data?.companyName) setCompanyName(data.data.companyName);
      if (data?.data?.showLogo !== undefined) setShowLogo(data.data.showLogo);
    } catch (_) {}
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authAPI.me();
      setUser(data.data);
      await fetchConfig();
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  }, [fetchConfig]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (credentials, isCustomer = false) => {
    const { data } = isCustomer
      ? await authAPI.customerLogin(credentials)
      : await authAPI.login(credentials);

    localStorage.setItem('token', data.data.token);
    if (data.data.refreshToken) localStorage.setItem('refreshToken', data.data.refreshToken);
    setUser(data.data.user);
    await fetchConfig();
    return data.data;
  };

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
  }, []);

  // Auto-logout after 5 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logout, INACTIVITY_TIMEOUT);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, logout]);

  const isMaster = user?.role === 'master';
  const isAdmin = user?.role === 'admin' || isMaster;
  const isStaff = user?.role === 'staff';
  const isCustomer = user?.role === 'customer';
  const isStaffOrAdmin = isAdmin || isStaff;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isMaster, isAdmin, isStaff, isCustomer, isStaffOrAdmin, loadUser, companyName, showLogo }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
